import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  useGetCitiesQuery,
  useCreateCityMutation,
  useUpdateCityMutation,
  useDeleteCityMutation,
  useGetCityRequestsQuery,
  useApproveCityRequestMutation,
  useRejectCityRequestMutation,
  City,
  CityRequest,
} from "../../../entities/admin-city";
import { useGetCountriesQuery, Country } from "../../../entities/admin-country";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

export const CitiesManagement = () => {
  const [selectedCity, setSelectedCity] = useState<City | null>(null);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [cityName, setCityName] = useState("");
  const [selectedCountryId, setSelectedCountryId] = useState<number | "">("");

  // Queries and mutations
  const { data: citiesData, isLoading: isLoadingCities } = useGetCitiesQuery();
  const { data: cityRequestsData, isLoading: isLoadingRequests } =
    useGetCityRequestsQuery();
  const { data: countriesData } = useGetCountriesQuery();

  // Helper function to get country name by ID
  const getCountryName = (countryId: number) => {
    const country = countriesData?.countries?.find((c) => c.id === countryId);
    return country?.name || "Не указана";
  };

  const [createCity, { isLoading: isCreating }] = useCreateCityMutation();
  const [updateCity, { isLoading: isUpdating }] = useUpdateCityMutation();
  const [deleteCity, { isLoading: isDeleting }] = useDeleteCityMutation();

  const [approveRequest, { isLoading: isApproving }] =
    useApproveCityRequestMutation();
  const [rejectRequest, { isLoading: isRejecting }] =
    useRejectCityRequestMutation();

  // Handlers
  const handleSelectCity = (city: City) => {
    setSelectedCity(city);
  };

  const handleOpenCreateDialog = () => {
    setCityName("");
    setSelectedCountryId("");
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (city: City) => {
    setSelectedCity(city);
    setCityName(city.name);
    setSelectedCountryId(city.countryId);
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (city: City) => {
    setSelectedCity(city);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  const handleCreateCity = async () => {
    if (!cityName.trim() || !selectedCountryId) return;

    try {
      await createCity({
        name: cityName.trim(),
        countryId: selectedCountryId as number,
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error creating city:", error);
    }
  };

  const handleUpdateCity = async () => {
    if (!selectedCity || !cityName.trim() || !selectedCountryId) return;

    try {
      await updateCity({
        id: selectedCity.id,
        body: {
          name: cityName.trim(),
          countryId: selectedCountryId as number,
        },
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error updating city:", error);
    }
  };

  const handleDeleteCity = async () => {
    if (!selectedCity) return;

    try {
      await deleteCity(selectedCity.id).unwrap();
      setSelectedCity(null);
      handleCloseDialogs();
    } catch (error) {
      console.error("Error deleting city:", error);
    }
  };

  const handleApproveRequest = async (request: CityRequest) => {
    try {
      await approveRequest(request.id).unwrap();
    } catch (error) {
      console.error("Error approving request:", error);
    }
  };

  const handleRejectRequest = async (request: CityRequest) => {
    try {
      await rejectRequest(request.id).unwrap();
    } catch (error) {
      console.error("Error rejecting request:", error);
    }
  };

  useEffect(() => {
    if (citiesData?.cities?.length && !selectedCity) {
      setSelectedCity(citiesData.cities[0]);
    }
  }, [citiesData, selectedCity]);

  // Render functions
  const renderCreateDialog = () => (
    <Dialog open={openCreateDialog} onClose={handleCloseDialogs}>
      <DialogTitle>Добавить город</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="dense">
          <InputLabel>Страна</InputLabel>
          <Select
            value={selectedCountryId}
            onChange={(e) => setSelectedCountryId(e.target.value as number)}
            label="Страна"
          >
            {countriesData?.countries?.map((country: Country) => (
              <MenuItem key={country.id} value={country.id}>
                {country.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          autoFocus
          margin="dense"
          label="Название города"
          fullWidth
          value={cityName}
          onChange={(e) => setCityName(e.target.value)}
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleCreateCity}
          disabled={!cityName.trim() || !selectedCountryId || isCreating}
        >
          {isCreating ? <CircularProgress size={24} /> : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog open={openEditDialog} onClose={handleCloseDialogs}>
      <DialogTitle>Редактировать город</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="dense">
          <InputLabel>Страна</InputLabel>
          <Select
            value={selectedCountryId}
            onChange={(e) => setSelectedCountryId(e.target.value as number)}
            label="Страна"
          >
            {countriesData?.countries?.map((country: Country) => (
              <MenuItem key={country.id} value={country.id}>
                {country.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          autoFocus
          margin="dense"
          label="Название города"
          fullWidth
          value={cityName}
          onChange={(e) => setCityName(e.target.value)}
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleUpdateCity}
          disabled={!cityName.trim() || !selectedCountryId || isUpdating}
        >
          {isUpdating ? <CircularProgress size={24} /> : "Сохранить"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDeleteDialog = () => (
    <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
      <DialogTitle>Удалить город</DialogTitle>
      <DialogContent>
        Вы уверены, что хотите удалить город "{selectedCity?.name}"?
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button onClick={handleDeleteCity} color="error" disabled={isDeleting}>
          {isDeleting ? <CircularProgress size={24} /> : "Удалить"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight={600} fontSize={24} mb={2}>
        Управление городами
      </Typography>

      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Cities Table */}
        <Paper sx={{ flex: 1, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography gutterBottom fontWeight={600} fontSize={18}>
              Города
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              Добавить
            </Button>
          </Box>

          {isLoadingCities ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Название города</TableCell>
                    <TableCell>Страна</TableCell>
                    <TableCell align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {citiesData?.cities?.map((city) => (
                    <TableRow
                      key={city.id}
                      sx={{
                        backgroundColor:
                          selectedCity?.id === city.id ? "#515151" : "inherit",
                        "&:hover": {
                          cursor: "pointer",
                          backgroundColor: "#414141",
                        },
                      }}
                      onClick={() => handleSelectCity(city)}
                    >
                      <TableCell>{city.name}</TableCell>
                      <TableCell>{getCountryName(city.countryId)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditDialog(city);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteDialog(city);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* City Requests */}
        <Paper sx={{ flex: 1, p: 2 }}>
          <Typography gutterBottom fontWeight={600} fontSize={18}>
            Запросы на добавление городов
          </Typography>

          {isLoadingRequests ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {cityRequestsData?.requests?.length ? (
                <List>
                  {cityRequestsData.requests.map((request) => (
                    <ListItem
                      key={request.id}
                      sx={{ borderBottom: "1px solid #333" }}
                      secondaryAction={
                        <Box>
                          <IconButton
                            color="success"
                            onClick={() => handleApproveRequest(request)}
                            disabled={isApproving || isRejecting}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleRejectRequest(request)}
                            disabled={isApproving || isRejecting}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={request.cityName}
                        secondary={`Создан: ${new Date(
                          request.createdAt
                        ).toLocaleDateString()}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">
                  Нет запросов на добавление городов
                </Typography>
              )}
            </>
          )}
        </Paper>
      </Box>

      {renderCreateDialog()}
      {renderEditDialog()}
      {renderDeleteDialog()}
    </Box>
  );
};
